import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import _ from 'lodash';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useModulesStore } from 'src/context/moduleContext';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import { useFeatures } from 'src/rbac/useFeatures';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';
import { useRiskSmartForm } from '../customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { ConditionalFieldsContext } from './ConditionalFieldsContext';
import {
  buildConditionSchemaContext,
  buildFieldConditionGraph,
  getEffectiveFieldConditions,
  getHiddenFields,
  getHiddenSections,
} from './conditionsGraph';

/**
 * Keeps track of which fields are conditionally hidden.
 * Note, needs to be used with a react hook form FormProvider as it needs access to the forms values
 *
 * `additionalConditionFields` lets the form-config preview register
 * session-only fields as condition sources so rules referencing them evaluate
 * before the field is persisted. Live forms omit it.
 */
export const ConditionalFieldsProvider = ({
  children,
  additionalConditionFields,
}: PropsWithChildren<{
  additionalConditionFields?: AdditionalConditionField[];
}>) => {
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const customisableData = useCustomisableFormDataContext();
  const { formFieldConfigurations } = customisableData;
  // TODO: possibly get parent type by other means to remove dependency on useRiskSmartForm
  const { parentType } = useRiskSmartForm();

  const { watch } = useFormContext();
  const enabledFeatures = useFeatures();
  const modules = useModulesStore((s) => s.modules);
  const currentValues = watch();
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');

  const formConfig = parentType
    ? getFormConfigRegistry(enabledFeatures, modules)[parentType]
    : undefined;

  const fieldConditionGraph = useMemo(
    () =>
      buildFieldConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        )
      ),
    [formFieldConfigurations, formConfig, formSectionsEnabled]
  );

  const helpers = useCustomDatasourceHelpers();
  const { customAttributeSchema } = customisableData;

  // Memoize the static schema context so filteringProps and freeTextKeys are not
  // recomputed on every form-value change (they depend on schema, not on field values).
  const schemaContext = useMemo(
    () =>
      parentType
        ? buildConditionSchemaContext({
            formId: parentType,
            customAttributeSchema,
            helpers,
            enabledFeatures,
            modules,
            additionalConditionFields,
          })
        : undefined,
    [
      parentType,
      customAttributeSchema,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
    ]
  );

  useEffect(() => {
    if (!parentType) {
      return;
    }

    const hiddenFieldsLatest = getHiddenFields({
      formId: parentType,
      customisableData,
      fieldConditionGraph,
      currentValues,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
      formSectionsEnabled,
      schemaContext,
    });

    const hiddenSectionsLatest = getHiddenSections({
      formId: parentType,
      customisableData,
      currentValues,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
      schemaContext,
    });

    setHiddenFields((prev) => {
      if (_.isEqual(prev, hiddenFieldsLatest)) {
        return prev;
      }

      return hiddenFieldsLatest;
    });

    setHiddenSections((prev) => {
      if (_.isEqual(prev, hiddenSectionsLatest)) {
        return prev;
      }

      return hiddenSectionsLatest;
    });
  }, [
    fieldConditionGraph,
    parentType,
    customisableData,
    currentValues,
    helpers,
    enabledFeatures,
    modules,
    additionalConditionFields,
    formSectionsEnabled,
    schemaContext,
  ]);

  return (
    <ConditionalFieldsContext.Provider value={{ hiddenFields, hiddenSections }}>
      {children}
    </ConditionalFieldsContext.Provider>
  );
};
